import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEOMeta from "@/components/base/SEOMeta";
import { aboutData } from "@/mocks/articles";

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: "关于我们" },
  ];

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: aboutData.brandName,
    url: "https://baozhaocsgo.cn",
    description: aboutData.brandIntro,
    email: aboutData.contact.email,
    sameAs: [
      "https://www.bilibili.com",
      "https://weibo.com",
    ],
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - B站暴躁少女CSGO视频专题站",
    description: aboutData.brandIntro,
    url: "https://baozhaocsgo.cn/about",
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <SEOMeta
        title="关于我们"
        description="了解B站暴躁少女CSGO视频专题站的品牌故事、口碑数据与联系方式。本站是CSGO视频内容聚合平台，汇聚暴躁少女的精彩操作、搞笑集锦与新手教学。"
        keywords="B站暴躁少女CSGO视频,关于我们,品牌介绍,联系方式,CSGO,暴躁少女"
        canonicalPath="/about"
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Hero */}
          <section className="py-10 md:py-14 text-center">
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-gamepad-line text-3xl"></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4">关于我们</h1>
            <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {aboutData.brandIntro}
            </p>
          </section>

          {/* Brand Story */}
          <section className="py-10 md:py-14 border-t border-background-200">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-5">
              品牌故事
            </h2>
            <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
              <p className="text-foreground-700 leading-relaxed text-base">
                {aboutData.brandStory}
              </p>
            </div>
          </section>

          {/* Reputation */}
          <section className="py-10 md:py-14 border-t border-background-200">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8">
              口碑数据
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {aboutData.reputation.map((item, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200 rounded-lg p-5 text-center"
                >
                  <div className="text-2xl md:text-3xl font-bold text-accent-500 mb-2">
                    {item.value}
                  </div>
                  <div className="text-sm text-foreground-600">{item.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-14 border-t border-background-200">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8">
              联系方式
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="bg-background-50 border border-background-200 rounded-lg p-5 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-sm text-foreground-600">{aboutData.contact.email}</p>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-lg p-5 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-bilibili-line text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">B站主页</h3>
                  <a
                    href="https://www.bilibili.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-sm text-primary-500 hover:text-primary-600 transition-colors"
                  >
                    暴躁少女
                  </a>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-lg p-5 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-weibo-line text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">微博</h3>
                  <p className="text-sm text-foreground-600">{aboutData.contact.weibo}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Disclaimer */}
          <section className="py-10 border-t border-background-200">
            <div className="bg-accent-50 border border-accent-200 rounded-lg p-5 md:p-6">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-sm"></i>
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {aboutData.siteInfo}
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}